import {
  FileQuestion,
  Inbox,
  Lock,
  PowerOff,
  ServerCrash,
  SearchX,
  type LucideIcon,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useCurrentSession } from '../../stores/session';
import { Button, buttonClassName } from '../Button';
import styles from './SystemState.module.css';

// SYS-01, SYS-03, SYS-04, SYS-05. One shape, four meanings, so they read as the
// same kind of moment rather than four unrelated screens.

type Medallion = 'neutral' | 'warning' | 'danger';

interface SystemStateProps {
  readonly icon: LucideIcon;
  readonly tone?: Medallion;
  readonly title: string;
  readonly body: string;
  readonly actions?: ReactNode;
  readonly footer?: ReactNode;
  /** Inside a table or a card rather than filling the page. */
  readonly isInset?: boolean;
  /** The state IS the page (SYS-03, SYS-04, SYS-09), so its title is the page's h1. */
  readonly isPageTitle?: boolean;
}

const MEDALLION_CLASS: Readonly<Record<Medallion, string>> = {
  neutral: '',
  warning: styles.medallionWarning,
  danger: styles.medallionDanger,
};

export function SystemState({
  icon: Icon,
  tone = 'neutral',
  title,
  body,
  actions,
  footer,
  isInset = false,
  isPageTitle = false,
}: SystemStateProps): ReactNode {
  const Title = isPageTitle ? 'h1' : 'h2';
  return (
    <div className={`${styles.state} ${isInset ? styles.stateInset : ''}`}>
      <span className={`${styles.medallion} ${MEDALLION_CLASS[tone]}`} aria-hidden>
        <Icon size={20} />
      </span>
      <div className={styles.text}>
        <Title className={styles.title}>{title}</Title>
        <p className={styles.body}>{body}</p>
      </div>
      {actions !== undefined && <div className={styles.actions}>{actions}</div>}
      {footer}
    </div>
  );
}

interface EmptyStateProps {
  readonly title: string;
  readonly body: string;
  /** 06-qol §5: an empty state ALWAYS contains the action that fills it. */
  readonly action: ReactNode;
  readonly isInset?: boolean;
}

/** SYS-01 — genuinely empty. Nothing has ever been here. */
export function EmptyState({ title, body, action, isInset }: EmptyStateProps): ReactNode {
  return (
    <SystemState icon={Inbox} title={title} body={body} actions={action} isInset={isInset} />
  );
}

interface FilteredEmptyStateProps {
  readonly onClearFilters: () => void;
  readonly filterCount: number;
  readonly isInset?: boolean;
}

/**
 * Filtered-to-empty is a DIFFERENT state from genuinely-empty (06-qol §5): there
 * is data, the filter is simply hiding all of it, so the copy says so and the
 * action clears the filter rather than offering to create something.
 */
export function FilteredEmptyState({
  onClearFilters,
  filterCount,
  isInset,
}: FilteredEmptyStateProps): ReactNode {
  return (
    <SystemState
      icon={SearchX}
      title="Nothing matches these filters"
      body={
        `${filterCount} ${filterCount === 1 ? 'filter is' : 'filters are'} active. ` +
        'Try broadening one, checking the spelling, or searching a different scope.'
      }
      actions={
        <Button variant="quiet" onClick={onClearFilters}>
          Clear filters
        </Button>
      }
      isInset={isInset}
    />
  );
}

/** Callers that mean "whoever runs this organisation's hiring" — the HR Lead, by name when known. */
export const YOUR_HR_LEAD = 'Your admin';

interface ForbiddenStateProps {
  /** What they tried to open, named plainly. */
  readonly resource: string;
  /** WHO to ask. A 403 that only says "denied" leaves the user nowhere to go. */
  readonly contact?: string;
  readonly isInset?: boolean;
}

/**
 * SYS-03 — 403, worded per §L26: "You don't have access to this. Ask {HR Lead name} if you need
 * it." The HR Lead's name comes with the session; without one it reads "your HR Lead".
 */
export function ForbiddenState({ resource, contact = YOUR_HR_LEAD, isInset = false }: ForbiddenStateProps): ReactNode {
  const hrLeadName = useCurrentSession()?.hrLeadName;
  const who = contact === YOUR_HR_LEAD ? (hrLeadName ?? 'your admin') : lowerArticle(contact);
  return (
    <SystemState
      icon={Lock}
      tone="warning"
      title="You don't have access to this"
      body={`Your role does not include ${resource}. Ask ${who} if you need it.`}
      isInset={isInset}
      isPageTitle={!isInset}
    />
  );
}

/** "The recruiter for this job" reads "the recruiter…" mid-sentence; a person's name is left alone. */
function lowerArticle(contact: string): string {
  return /^(The|Your) /.test(contact) ? `${contact.charAt(0).toLowerCase()}${contact.slice(1)}` : contact;
}

/** SYS-04 — 404. */
export function NotFoundState({ onGoBack }: { onGoBack?: () => void }): ReactNode {
  return (
    <SystemState
      icon={FileQuestion}
      title="This page does not exist"
      body="The link may be out of date, or the record may have been merged or removed."
      isPageTitle
      actions={
        onGoBack && (
          <Button variant="quiet" onClick={onGoBack}>
            Go back
          </Button>
        )
      }
    />
  );
}

/**
 * SYS-09 (R1, §L28) — a deep link into a module switched off in Master settings. Calm, never an
 * error: the feature exists and can come back. The HR Lead is offered the way to turn it on.
 */
export function SwitchedOffState({ feature, canOpenSettings }: { readonly feature: string; readonly canOpenSettings: boolean }): ReactNode {
  return (
    <SystemState
      icon={PowerOff}
      title="This feature is switched off"
      body={`“${feature}” is switched off in Master settings, so this page isn't available. An admin can turn it on.`}
      isPageTitle
      actions={
        // A real link, so Ctrl-click opens Master settings in a new tab (06-qol §8).
        canOpenSettings ? (
          <Link to="/admin/master-settings" className={buttonClassName('quiet')}>
            Open Master settings
          </Link>
        ) : undefined
      }
    />
  );
}

interface ServerErrorStateProps {
  /** Support cannot trace an error without it, and asking the user to reproduce
   *  the fault is how a bug report dies. */
  readonly requestId?: string;
  readonly onRetry?: () => void;
}

/** SYS-05 — 500. */
export function ServerErrorState({ requestId, onRetry }: ServerErrorStateProps): ReactNode {
  return (
    <SystemState
      icon={ServerCrash}
      tone="danger"
      title="Something went wrong at our end"
      body="This is not your fault and nothing you did has been lost. Try again in a moment."
      actions={
        onRetry && (
          <Button variant="quiet" onClick={onRetry}>
            Try again
          </Button>
        )
      }
      footer={
        requestId !== undefined && (
          <span className={styles.reference}>Reference {requestId}</span>
        )
      }
    />
  );
}
